import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Controls & Automation", href: "#control" },
  { label: "Cooling Systems", href: "#cooling" },
  { label: "Heating Systems", href: "#heating" },
  { label: "About Us", href: "#about", children: [{ label: "Contact Us", href: "#easy" }] },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    setAboutOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Top info bar */}
      <div className="w-full hidden lg:block" style={{ background: "#1a5f8a" }}>
        <div className="max-w-7xl mx-auto px-8 py-2.5 flex items-center justify-end gap-8 text-xs text-white/90">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-2-fill text-white/70"></i>
            </div>
            <span>331 38th Street Brooklyn, NY 11232</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-mail-fill text-white/70"></i>
            </div>
            <span>support@thermalxpert.nyc</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-white/70"></i>
            </div>
            <span>(929) 678-1640</span>
          </div>
          <a
            href="https://book.housecallpro.com/book/THERMALXPERT/82bac7c9463e4df29b9d2e77332e7c39?v2=true"
            target="_blank"
            rel="nofollow noreferrer"
            className="px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-all hover:scale-105 text-white"
            style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}
          >
            Schedule Online
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className="w-full sticky top-0 z-50 transition-all duration-500"
        style={{
          background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          borderBottom: scrolled ? "1px solid rgba(74,159,212,0.15)" : "1px solid rgba(74,159,212,0.08)",
          boxShadow: scrolled ? "0 2px 40px rgba(74,159,212,0.12)" : "none",
        }}
      >
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <button onClick={() => scrollTo("#home")} className="cursor-pointer flex-shrink-0">
            <img
              src="https://thermalxpert.nyc/wp-content/uploads/2024/03/ThermalExpert-Color-TransperentBG-300x51-1.png"
              alt="ThermalXpert"
              className="h-9 w-auto object-contain"
            />
          </button>

          <ul className="hidden md:flex items-center gap-7 text-sm font-medium text-gray-600">
            {navLinks.map((link) =>
              link.children ? (
                <li key={link.label} className="relative">
                  <button
                    onClick={() => setAboutOpen((v) => !v)}
                    className="flex items-center gap-1 hover:text-[#4a9fd4] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${aboutOpen ? "rotate-180" : ""}`}></i>
                  </button>
                  {aboutOpen && (
                    <div
                      className="absolute top-full left-0 mt-3 rounded-xl py-2 min-w-[150px]"
                      style={{ background: "#fff", border: "1px solid rgba(74,159,212,0.15)", boxShadow: "0 8px 32px rgba(74,159,212,0.12)" }}
                    >
                      {link.children.map((child) => (
                        <button
                          key={child.label}
                          onClick={() => scrollTo(child.href)}
                          className="block w-full text-left px-4 py-2.5 text-sm text-gray-600 hover:text-[#4a9fd4] hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ) : (
                <li key={link.label}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="hover:text-[#4a9fd4] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                  </button>
                </li>
              )
            )}
            <li>
              <Link
                to="/register"
                className="px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:scale-105"
                style={{ color: "#2d7ab5", background: "rgba(74,159,212,0.1)", border: "1px solid rgba(74,159,212,0.25)" }}
              >
                Register
              </Link>
            </li>
            <li>
              <button
                onClick={() => scrollTo("#easy")}
                className="px-5 py-2.5 rounded-full text-sm font-bold text-white whitespace-nowrap cursor-pointer transition-all hover:scale-105 hover:opacity-90"
                style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
              >
                Get a Quote
              </button>
            </li>
          </ul>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer"
            style={{ background: "rgba(74,159,212,0.08)", border: "1px solid rgba(74,159,212,0.2)" }}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <i className={`text-lg text-[#4a9fd4] ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div
            className="md:hidden px-6 py-4 flex flex-col gap-1 bg-white"
            style={{ borderTop: "1px solid rgba(74,159,212,0.1)" }}
          >
            {navLinks.map((link) => (
              <div key={link.label}>
                <button
                  onClick={() => scrollTo(link.href)}
                  className="block w-full text-left text-sm py-2.5 cursor-pointer text-gray-600 hover:text-[#4a9fd4] transition-colors"
                >
                  {link.label}
                </button>
                {link.children?.map((child) => (
                  <button
                    key={child.label}
                    onClick={() => scrollTo(child.href)}
                    className="block w-full text-left text-sm py-2 pl-4 cursor-pointer text-gray-400 hover:text-[#4a9fd4] transition-colors"
                  >
                    {child.label}
                  </button>
                ))}
              </div>
            ))}
            <Link
              to="/register"
              className="block text-sm py-2.5 cursor-pointer font-bold whitespace-nowrap"
              style={{ color: "#4a9fd4" }}
              onClick={() => setMobileOpen(false)}
            >
              Register as Customer
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}
